import React from "react";
import ReportPagination from "./ReportPagination";
import ReportSummaryCard from "./ReportSummaryCard";

/**
 * ReportFooter — Reusable Flexbox Bottom Bar for Reports
 * 
 * Purpose:
 * Transforms stacked footer layouts (Image 1) into a clean, unified single-row
 * flexbox layout (Image 2) with pagination controls on the left and summary
 * metric badges on the right.
 * 
 * Layout Structure:
 * - Outer container: flex flex-row flex-wrap items-center justify-between
 * - Left side: Standalone bordered pagination card
 * - Right side: Summary total badge cards (Total Deposits, Withdrawals, Collections)
 * 
 * Props:
 * Option A (All-in-one props):
 * - currentPage: Number (1-based)
 * - totalPages: Number
 * - totalCount: Number (optional)
 * - rowsPerPage: Number
 * - onPageChange: (page) => void
 * - onRowsPerPageChange: (limit) => void
 * - pageSizeOptions: Array of numbers (e.g. [25, 50, 100, 200])
 * - totals: Array of { label, value, variant, formatCurrency }
 * 
 * Option B (Slot composition props):
 * - pagination: Custom pagination ReactNode
 * - summary: Custom summary badges ReactNode
 * - children: Fallback children
 * - className: Custom container classes
 */
const ReportFooter = ({
  // Option A: Pagination props
  currentPage,
  totalPages,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  pageSizeOptions,

  // Option A: Totals array props
  totals = [],

  // Option B: Direct slot props
  pagination,
  summary,
  children,
  className = "",
}) => {
  // Determine pagination content: direct slot OR built from props
  const hasPaginationProps = onPageChange !== undefined || currentPage !== undefined;
  const paginationNode =
    pagination ||
    (hasPaginationProps && (
      <ReportPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        pageSizeOptions={pageSizeOptions}
      />
    ));

  // Determine summary content: direct slot OR built from totals array
  const summaryNode =
    summary ||
    (totals && totals.length > 0 && (
      <div className="flex items-center gap-3">
        {totals.map((card, idx) => (
          <ReportSummaryCard
            key={card.label || idx}
            label={card.label}
            value={card.value}
            variant={card.variant || "primary"}
            formatCurrency={card.formatCurrency !== false}
          />
        ))}
      </div>
    ));

  return (
    <div
      className={[
        "flex flex-row flex-wrap items-center justify-between gap-4 mt-3 w-full",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Left Side: Standalone Bordered Pagination Box */}
      {paginationNode && (
        <div className="flex items-center gap-3">
          {paginationNode}
        </div>
      )}

      {/* Right Side: Metric Totals (Total Deposits, Collections, etc.) */}
      {summaryNode && (
        <div className="flex items-center gap-3 ml-auto">
          {summaryNode}
        </div>
      )}

      {children}
    </div>
  );
};

export default ReportFooter;
